import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { sharedStyles, createGlassCard, createButton } from './shared/sharedStyles';
import Navbar from './shared/Navbar';
import { useApi } from '../utils/api';

const Success = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const { makeRequest } = useApi();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifySubscription = async (retries = 10, delay = 1000) => {
      // Wait for Clerk to load before checking authentication
      if (!isLoaded) {
        console.log('⏳ Waiting for Clerk to load...');
        return;
      }

      if (!isSignedIn) {
        console.log('❌ User not signed in, redirecting to dashboard');
        navigate('/dashboard');
        return;
      }

      console.log(`🔍 Verifying subscription (${retries} retries left)...`);

      try {
        // Get subscription status from backend using API utility
        const data = await makeRequest('billing/subscription-status');
        console.log('📊 Subscription status:', data);

        // Check if subscription is active
        if (data.has_active_subscription) {
          // Success! Subscription is active
          console.log('✅ Subscription verified successfully!');
          setSubscriptionData(data);
          setLoading(false);
          return;
        }

        // Webhook might still be processing, retry with exponential backoff
        if (retries > 0) {
          console.log(`⏳ Subscription not active yet, retrying in ${delay}ms... (${retries} retries left)`);
          setTimeout(() => verifySubscription(retries - 1, delay), delay);
        } else {
          // All retries exhausted
          console.error('❌ Subscription verification failed after 10 retries');
          setError("Subscription not yet active. Please refresh the page in a moment or contact support if the issue persists.");
          setLoading(false);
        }
      } catch (err) {
        console.error('❌ Error fetching subscription:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          name: err.name
        });
        setError(err.message || 'Failed to verify subscription. Please try refreshing the page.');
        setLoading(false);
      }
    };

    verifySubscription();
  }, [isSignedIn, isLoaded, navigate, makeRequest]);

  const getTierDisplayName = (tier) => {
    const tierMap = {
      'starter': 'Starter',
      'pro': 'Pro',
    };
    return tierMap[tier] || tier;
  };

  const handleContinue = () => {
    navigate('/intake-form');
  };

  const handleViewDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div style={sharedStyles.pageContainer}>
      <Navbar />

      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1.5rem',
        maxWidth: '800px',
        margin: '0 auto',
        width: '100%',
      }}>
        {loading ? (
          <div style={createGlassCard('default', {
            padding: '3rem 2rem',
            textAlign: 'center',
          })}>
            <div style={{
              fontSize: '2rem',
              marginBottom: '1rem',
            }}>
              ⏳
            </div>
            <h2 style={{
              ...sharedStyles.subheading,
              fontSize: '1.5rem',
              marginBottom: '0.5rem',
            }}>
              Confirming your subscription...
            </h2>
            <p style={sharedStyles.bodyText}>
              Please wait while we verify your payment.
            </p>
          </div>
        ) : error ? (
          <div style={createGlassCard('default', {
            padding: '3rem 2rem',
            textAlign: 'center',
          })}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem',
            }}>
              ⚠️
            </div>
            <h2 style={{
              ...sharedStyles.subheading,
              fontSize: '1.5rem',
              marginBottom: '0.5rem',
              color: sharedStyles.colors.error,
            }}>
              Something went wrong
            </h2>
            <p style={{
              ...sharedStyles.bodyText,
              marginBottom: '2rem',
            }}>
              {error}
            </p>
            <button
              onClick={() => navigate('/pricing')}
              style={createButton('primary')}
            >
              Back to Pricing
            </button>
          </div>
        ) : (
          <div style={createGlassCard('default', {
            padding: '3rem 2rem',
            textAlign: 'center',
          })}>
            {/* Success Icon */}
            <div style={{
              fontSize: '4rem',
              marginBottom: '1.5rem',
            }}>
              🎉
            </div>

            {/* Header */}
            <h1 style={{
              ...sharedStyles.heading,
              fontSize: '2.5rem',
              marginBottom: '1rem',
            }}>
              Welcome to Conviviality!
            </h1>

            {/* Subscription Details */}
            {subscriptionData && (
              <div style={{
                background: sharedStyles.colors.primaryLight,
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem',
              }}>
                <p style={{
                  ...sharedStyles.bodyText,
                  fontSize: '1.125rem',
                  marginBottom: '0.75rem',
                }}>
                  You're now subscribed to the{' '}
                  <strong style={{ color: sharedStyles.colors.primary }}>
                    {getTierDisplayName(subscriptionData.subscription_tier)}
                    {subscriptionData.is_lifetime && ' (Lifetime)'}
                  </strong>
                  {' '}plan
                </p>
                <p style={sharedStyles.bodyText}>
                  You have{' '}
                  <strong>{subscriptionData.monthly_reports_limit}</strong>
                  {' '}AI-powered guest reports available per month
                </p>
              </div>
            )}

            {/* Feature Highlights */}
            <div style={{
              textAlign: 'left',
              marginBottom: '2rem',
              padding: '0 1rem',
            }}>
              <h3 style={{
                ...sharedStyles.subheading,
                fontSize: '1.125rem',
                marginBottom: '1rem',
                textAlign: 'center',
              }}>
                What's included:
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
              }}>
                {subscriptionData?.subscription_tier === 'starter' ? (
                  <>
                    <li style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.95rem',
                    }}>
                      <span style={{ color: sharedStyles.colors.success, fontSize: '1.25rem' }}>✓</span>
                      <span style={{ color: sharedStyles.colors.textPrimary }}>AI-powered guest discovery with relevance scoring</span>
                    </li>
                    <li style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.95rem',
                    }}>
                      <span style={{ color: sharedStyles.colors.success, fontSize: '1.25rem' }}>✓</span>
                      <span style={{ color: sharedStyles.colors.textPrimary }}>Guest profiles with expertise areas</span>
                    </li>
                    <li style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.95rem',
                    }}>
                      <span style={{ color: sharedStyles.colors.success, fontSize: '1.25rem' }}>✓</span>
                      <span style={{ color: sharedStyles.colors.textPrimary }}>Show-specific recommendations and summaries</span>
                    </li>
                  </>
                ) : (
                  <>
                    <li style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.95rem',
                    }}>
                      <span style={{ color: sharedStyles.colors.success, fontSize: '1.25rem' }}>✓</span>
                      <span style={{ color: sharedStyles.colors.textPrimary }}>Everything in Starter, plus:</span>
                    </li>
                    <li style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.95rem',
                    }}>
                      <span style={{ color: sharedStyles.colors.success, fontSize: '1.25rem' }}>✓</span>
                      <span style={{ color: sharedStyles.colors.textPrimary }}>Contact information (email, LinkedIn, X/Twitter)</span>
                    </li>
                    <li style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.95rem',
                    }}>
                      <span style={{ color: sharedStyles.colors.success, fontSize: '1.25rem' }}>✓</span>
                      <span style={{ color: sharedStyles.colors.textPrimary }}>AI-generated personalized outreach pitches</span>
                    </li>
                    <li style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.95rem',
                    }}>
                      <span style={{ color: sharedStyles.colors.success, fontSize: '1.25rem' }}>✓</span>
                      <span style={{ color: sharedStyles.colors.textPrimary }}>Bring Your Own API Key for unlimited reports</span>
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* CTA Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              marginTop: '2rem',
            }}>
              <button
                onClick={handleContinue}
                style={createButton('primary', {
                  width: '100%',
                  justifyContent: 'center',
                  padding: '1rem',
                  fontSize: '1rem',
                })}
              >
                Create Your First Report
              </button>
              <button
                onClick={handleViewDashboard}
                style={createButton('outline', {
                  width: '100%',
                  justifyContent: 'center',
                  padding: '1rem',
                  fontSize: '1rem',
                })}
              >
                View Dashboard
              </button>
            </div>

            {/* Help Text */}
            <p style={{
              ...sharedStyles.captionText,
              marginTop: '2rem',
              fontSize: '0.85rem',
            }}>
              Need help getting started?{' '}
              <a
                href="mailto:support@conviviality.ai"
                style={{
                  color: sharedStyles.colors.primary,
                  textDecoration: 'none',
                  fontWeight: '500',
                }}
              >
                Contact support
              </a>
              {' '}for assistance.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Success;
